import { useCallback, useRef } from "react";
import { getFontEmbedCSS, toCanvas } from "html-to-image";

import { faro } from "@grafana/faro-react";

export type UseScreenshotResult = {
  captureAndDownload: (filename: string) => Promise<void>;
  ref: React.RefObject<HTMLDivElement | null>;
};

export type UseScreenshotOptions = {
  preprocessElement?: (element: HTMLElement) => Promise<void> | void;
};

const resolveCSSVariablesInSVG = (element: HTMLElement) => {
  // Find all SVG elements with fill or stroke attributes using CSS variables
  const svgElements = element.querySelectorAll("svg [fill], svg [stroke]");

  svgElements.forEach((el) => {
    const svgEl = el as SVGElement;
    const computedStyle = window.getComputedStyle(svgEl);

    // Resolve fill color
    const fillAttr = svgEl.getAttribute("fill");

    if (fillAttr && (fillAttr.includes("var(") || fillAttr.includes("hsl("))) {
      const computedFill = computedStyle.fill;

      if (computedFill && computedFill !== "none") {
        svgEl.setAttribute("fill", computedFill);
      }
    }

    // Resolve stroke color
    const strokeAttr = svgEl.getAttribute("stroke");

    if (
      strokeAttr &&
      (strokeAttr.includes("var(") || strokeAttr.includes("hsl("))
    ) {
      const computedStroke = computedStyle.stroke;

      if (computedStroke && computedStroke !== "none") {
        svgEl.setAttribute("stroke", computedStroke);
      }
    }
  });

  // Handle SVG path elements (for Recharts Line components)
  const pathElements = element.querySelectorAll("svg path");
  pathElements.forEach((el) => {
    const pathEl = el as SVGPathElement;
    const computedStyle = window.getComputedStyle(pathEl);

    // Get stroke attribute
    const strokeAttr = pathEl.getAttribute("stroke");

    // Check if this path has a stroke (it's a line, not just a shape)
    if (strokeAttr && strokeAttr !== "none") {
      // Remove stroke-dasharray which can make lines invisible in screenshots
      if (pathEl.hasAttribute("stroke-dasharray")) {
        pathEl.removeAttribute("stroke-dasharray");
      }

      // Also clear style.strokeDasharray
      if (pathEl.style.strokeDasharray) {
        pathEl.style.strokeDasharray = "";
      }

      // Get computed stroke color
      const computedStroke = computedStyle.stroke;

      // Resolve CSS variable colors in attribute
      if (
        strokeAttr.includes("var(") ||
        strokeAttr.includes("hsl(") ||
        strokeAttr.includes("color-mix")
      ) {
        if (computedStroke && computedStroke !== "none") {
          pathEl.setAttribute("stroke", computedStroke);
        }
      }

      // CRITICAL: Also resolve inline style.stroke property (Recharts sets both!)
      const inlineStroke = pathEl.style.stroke;

      if (
        inlineStroke &&
        (inlineStroke.includes("var(") ||
          inlineStroke.includes("hsl(var") ||
          inlineStroke.includes("color-mix"))
      ) {
        if (computedStroke && computedStroke !== "none") {
          pathEl.style.stroke = computedStroke;
        }
      }
    }

    // Resolve fill color for paths
    const fillAttr = pathEl.getAttribute("fill");

    if (
      fillAttr &&
      (fillAttr.includes("var(") ||
        fillAttr.includes("hsl(") ||
        fillAttr.includes("color-mix"))
    ) {
      const computedFill = computedStyle.fill;

      if (computedFill && computedFill !== "none") {
        pathEl.setAttribute("fill", computedFill);
      }
    }

    // Also resolve inline style.fill property
    const inlineFill = pathEl.style.fill;

    if (
      inlineFill &&
      (inlineFill.includes("var(") ||
        inlineFill.includes("hsl(var") ||
        inlineFill.includes("color-mix"))
    ) {
      const computedFill = computedStyle.fill;

      if (computedFill && computedFill !== "none") {
        pathEl.style.fill = computedFill;
      }
    }
  });

  // Handle SVG circle elements (for Recharts dots)
  const circleElements = element.querySelectorAll("svg circle");
  circleElements.forEach((el) => {
    const circleEl = el as SVGCircleElement;
    const computedStyle = window.getComputedStyle(circleEl);

    // Resolve stroke color
    const strokeAttr = circleEl.getAttribute("stroke");

    if (
      strokeAttr &&
      (strokeAttr.includes("var(") ||
        strokeAttr.includes("hsl(") ||
        strokeAttr.includes("color-mix"))
    ) {
      const computedStroke = computedStyle.stroke;

      if (computedStroke && computedStroke !== "none") {
        circleEl.setAttribute("stroke", computedStroke);
      }
    }

    // Resolve inline style.stroke
    const inlineStroke = circleEl.style.stroke;

    if (
      inlineStroke &&
      (inlineStroke.includes("var(") ||
        inlineStroke.includes("hsl(var") ||
        inlineStroke.includes("color-mix"))
    ) {
      const computedStroke = computedStyle.stroke;

      if (computedStroke && computedStroke !== "none") {
        circleEl.style.stroke = computedStroke;
      }
    }

    // Resolve fill color
    const fillAttr = circleEl.getAttribute("fill");

    if (
      fillAttr &&
      (fillAttr.includes("var(") ||
        fillAttr.includes("hsl(") ||
        fillAttr.includes("color-mix"))
    ) {
      const computedFill = computedStyle.fill;

      if (computedFill && computedFill !== "none") {
        circleEl.setAttribute("fill", computedFill);
      }
    }

    // Resolve inline style.fill
    const inlineFill = circleEl.style.fill;

    if (
      inlineFill &&
      (inlineFill.includes("var(") ||
        inlineFill.includes("hsl(var") ||
        inlineFill.includes("color-mix"))
    ) {
      const computedFill = computedStyle.fill;

      if (computedFill && computedFill !== "none") {
        circleEl.style.fill = computedFill;
      }
    }
  });

  const textElements = element.querySelectorAll("svg text, svg tspan");
  textElements.forEach((el) => {
    const textEl = el as SVGTextElement | SVGTSpanElement;
    const computedStyle = window.getComputedStyle(textEl);

    const computedFill = computedStyle.fill;

    if (
      computedFill &&
      computedFill !== "none" &&
      computedFill !== "rgba(0, 0, 0, 0)"
    ) {
      textEl.setAttribute("fill", computedFill);
      textEl.style.fill = computedFill;
    }

    const fontSize = computedStyle.fontSize;
    const fontWeight = computedStyle.fontWeight;
    const fontFamily = computedStyle.fontFamily;
    const fontStyle = computedStyle.fontStyle;
    const letterSpacing = computedStyle.letterSpacing;

    if (fontSize) textEl.style.fontSize = fontSize;

    if (fontWeight && fontWeight !== "normal")
      textEl.style.fontWeight = fontWeight;

    if (fontFamily) textEl.style.fontFamily = fontFamily;

    if (fontStyle && fontStyle !== "normal") textEl.style.fontStyle = fontStyle;

    if (letterSpacing && letterSpacing !== "normal")
      textEl.style.letterSpacing = letterSpacing;
  });

  // Resolve CSS for foreignObject text elements
  const foreignObjectElements = element.querySelectorAll("svg foreignObject *");
  foreignObjectElements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    const computedStyle = window.getComputedStyle(htmlEl);

    // Remove default margins from p tags that can cause spacing issues
    if (htmlEl.tagName === "P") {
      htmlEl.style.margin = "0";
    }

    // Get computed color and convert to inline style
    const color = computedStyle.color;

    if (color && color !== "rgba(0, 0, 0, 0)") {
      htmlEl.style.color = color;
    }

    // Get font properties - always set them explicitly
    const fontSize = computedStyle.fontSize;
    const fontWeight = computedStyle.fontWeight;
    const fontFamily = computedStyle.fontFamily;
    const lineHeight = computedStyle.lineHeight;
    const letterSpacing = computedStyle.letterSpacing;

    // Always set font properties explicitly, even if default values
    if (fontSize) htmlEl.style.fontSize = fontSize;

    if (fontWeight) htmlEl.style.fontWeight = fontWeight;

    if (fontFamily) htmlEl.style.fontFamily = fontFamily;

    if (lineHeight) htmlEl.style.lineHeight = lineHeight;

    if (letterSpacing && letterSpacing !== "normal")
      htmlEl.style.letterSpacing = letterSpacing;

    // Get layout properties (critical for flex centering in pie chart)
    const display = computedStyle.display;
    const flexDirection = computedStyle.flexDirection;
    const alignItems = computedStyle.alignItems;
    const justifyContent = computedStyle.justifyContent;
    const width = computedStyle.width;
    const height = computedStyle.height;
    const textAlign = computedStyle.textAlign;
    const padding = computedStyle.padding;
    const paddingLeft = computedStyle.paddingLeft;
    const paddingRight = computedStyle.paddingRight;
    const gap = computedStyle.gap;

    if (display) htmlEl.style.display = display;

    if (flexDirection && flexDirection !== "row")
      htmlEl.style.flexDirection = flexDirection;

    if (alignItems && alignItems !== "normal")
      htmlEl.style.alignItems = alignItems;

    if (justifyContent && justifyContent !== "normal")
      htmlEl.style.justifyContent = justifyContent;

    if (width) htmlEl.style.width = width;

    if (height) htmlEl.style.height = height;

    // Always set textAlign explicitly (including "center")
    if (textAlign) htmlEl.style.textAlign = textAlign;

    if (padding && padding !== "0px") htmlEl.style.padding = padding;

    if (paddingLeft && paddingLeft !== "0px")
      htmlEl.style.paddingLeft = paddingLeft;

    if (paddingRight && paddingRight !== "0px")
      htmlEl.style.paddingRight = paddingRight;

    if (gap && gap !== "normal" && gap !== "0px") htmlEl.style.gap = gap;

    // Handle text wrapping properties carefully
    const wordBreak = computedStyle.wordBreak;
    const overflowWrap = computedStyle.overflowWrap;

    if (wordBreak && wordBreak !== "normal") htmlEl.style.wordBreak = wordBreak;

    if (overflowWrap && overflowWrap !== "normal")
      htmlEl.style.overflowWrap = overflowWrap;

    // For elements with line-clamp, preserve it but don't let it add extra margin
    const WebkitLineClamp =
      computedStyle.getPropertyValue("-webkit-line-clamp");

    if (WebkitLineClamp && WebkitLineClamp !== "none") {
      // line-clamp requires display: -webkit-box and overflow: hidden
      htmlEl.style.display = "-webkit-box";
      htmlEl.style.setProperty("-webkit-line-clamp", WebkitLineClamp);
      htmlEl.style.setProperty("-webkit-box-orient", "vertical");
      htmlEl.style.overflow = "hidden";
      // Remove any margin that might be added
      htmlEl.style.margin = "0";
    }
  });
};

export const useScreenshot = (
  options?: UseScreenshotOptions,
): UseScreenshotResult => {
  const ref = useRef<HTMLDivElement>(null);

  const captureAndDownload = useCallback(
    async (filename: string) => {
      if (!ref.current) return;

      try {
        // Clone the node to avoid modifying the actual DOM
        const clonedNode = ref.current.cloneNode(true) as HTMLElement;

        // Create a temporary container off-screen
        const tempContainer = document.createElement("div");
        tempContainer.style.position = "absolute";
        tempContainer.style.left = "-9999px";
        tempContainer.style.top = "-9999px";
        // Make sure the container has a defined width so ResponsiveContainer can measure it
        tempContainer.style.width = `${ref.current.offsetWidth || ref.current.clientWidth}px`;
        tempContainer.appendChild(clonedNode);
        document.body.appendChild(tempContainer);

        // Force a layout recalculation
        void tempContainer.offsetHeight;

        // Remove elements marked as exclude-from-screenshot
        const excludedElements = clonedNode.querySelectorAll(
          "[data-exclude-from-screenshot]",
        );
        excludedElements.forEach((el) => el.remove());

        // Resolve CSS variables in SVG elements (required for chart colors)
        resolveCSSVariablesInSVG(clonedNode);

        // Wait longer to allow ResponsiveContainer's ResizeObserver to trigger
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Run custom preprocessing if provided
        if (options?.preprocessElement) {
          await options.preprocessElement(clonedNode);
        }

        // Additional small delay to ensure any preprocessing is applied
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Pre-fetch fonts to ensure proper text rendering
        const fontEmbedCSS = await getFontEmbedCSS(clonedNode);

        // Capture the canvas
        const canvas = await toCanvas(clonedNode, {
          cacheBust: false,
          pixelRatio: 2,
          backgroundColor: "#ffffff",
          fontEmbedCSS,
        });

        // Clean up
        document.body.removeChild(tempContainer);

        // Download
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.download = `${filename}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
          }
        }, "image/png");
      } catch (error) {
        faro.api.pushError(error as Error, {
          type: "screenshot",
          context: {
            hook: "useScreenshot",
            filename,
            timestamp: new Date().toISOString(),
          },
        });
        throw error;
      }
    },
    [options],
  );

  return { captureAndDownload, ref };
};
